import React, { useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ID_PLACEHOLDER,
  LEVELS,
  OTTER_BODY,
  OTTER_BODY_KLEIN,
  OTTER_VIEWBOX,
  PARTS,
} from './otterArt.generated.js';
import { normalizeOtterLevel } from '../../constants/otterLevels.js';

/**
 * OtterFigure — der Otter-Avatar (Aura v2, Okt 2026): Linien-Otter in Tinte,
 * Teile als flache Farbflaechen unter der Tintenkontur ("Sticker"), dahinter
 * optional die Level-Welle.
 *
 *   <OtterFigure level={3} parts={['kette', 'sonnenbrille']} size={104} title="Lena, Level 3" />
 *
 * - `level` 1..6 zeichnet die Wellen-Szene hinter den Otter (null = nur Otter).
 * - `parts` sind Accessory-Schluessel des Backend-Katalogs
 *   (backend/src/constants/otterCatalog.js). Unbekannte werden ignoriert,
 *   doppelte nur einmal gezeichnet. Die Ebenen aller Teile werden gemeinsam
 *   nach z sortiert (gleiches z: Reihenfolge in `parts`).
 * - `variant` "klein" hat dickere Striche fuer kleine Groessen; ohne Angabe
 *   automatisch unter 72 px. Wirkt nur mit Level (die Level-viewBox ist groesser,
 *   der Otter darin also kleiner); der Otter allein bleibt wie gezeichnet.
 * - `title` macht die Figur zu einem Bild mit Namen, ohne `title` ist sie Deko
 *   (aria-hidden).
 *
 * Farben kommen nur aus var(--eo-*) (src/index.css, alle Theme-Bloecke).
 * CD-Ausnahme: Die Sticker-Farben gibt es NUR am Otter-Avatar. Das Logo
 * (OtterLogo, LogoSplash) bekommt nie Farbe und nie Teile.
 *
 * Gerendert per dangerouslySetInnerHTML: Die Strings stammen ausschliesslich
 * aus otterArt.generated.js (vom Generator in design/otter-art/ erzeugt,
 * statisch, ohne Nutzereingaben). Aus den Props fliesst nur in Auswahl und
 * Reihenfolge ein, nie Text ins Markup.
 */
export const KLEIN_BELOW_PX = 72;

const cleanId = (raw) => `eo-${String(raw).replace(/[^a-zA-Z0-9_-]/g, '')}-`;

export const buildOtterMarkup = ({ level, parts, variant }) => {
  const lvl = normalizeOtterLevel(level);
  const scene = lvl ? LEVELS[lvl] : null;
  const klein = variant === 'klein';

  const seen = new Set();
  const known = [];
  (Array.isArray(parts) ? parts : []).forEach((key) => {
    if (typeof key !== 'string' || seen.has(key)) return;
    if (!Object.prototype.hasOwnProperty.call(PARTS, key)) return;
    seen.add(key);
    known.push(PARTS[key]);
  });

  const under = known.map((p) => p.under || '').join('');
  const layers = known
    .flatMap((p, partIndex) => p.layers.map((l) => ({ ...l, partIndex })))
    .sort((a, b) => a.z - b.z || a.partIndex - b.partIndex)
    .map((l) => l.svg)
    .join('');

  const body = scene && klein ? OTTER_BODY_KLEIN : OTTER_BODY;
  const aura = scene ? (klein ? scene.klein : scene.normal) : '';
  return {
    level: lvl,
    viewBox: scene ? scene.viewBox : OTTER_VIEWBOX,
    markup: aura + under + body + (layers ? `<g class="teile">${layers}</g>` : ''),
  };
};

const OtterFigure = ({ level = null, parts = [], size = 104, variant, title, className = '', style }) => {
  const prefix = cleanId(useId());
  const resolvedVariant = variant || (Number(size) < KLEIN_BELOW_PX ? 'klein' : 'normal');
  const partsKey = Array.isArray(parts) ? parts.join('|') : '';

  const { viewBox, markup, level: lvl } = useMemo(
    () => buildOtterMarkup({ level, parts, variant: resolvedVariant }),
    // partsKey statt parts: ein neues Array mit gleichem Inhalt soll nicht neu bauen
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [level, partsKey, resolvedVariant]
  );
  const html = useMemo(() => markup.split(ID_PLACEHOLDER).join(prefix), [markup, prefix]);

  const a11y = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': 'true', focusable: 'false' };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      className={`eo-otter ${className}`.trim()}
      style={style}
      data-variant={resolvedVariant}
      data-otter-level={lvl || undefined}
      {...a11y}
      // Statischer, generierter Inhalt (siehe Kommentar oben), keine Nutzereingaben.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

OtterFigure.propTypes = {
  level: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  parts: PropTypes.arrayOf(PropTypes.string),
  size: PropTypes.number,
  variant: PropTypes.oneOf(['normal', 'klein']),
  title: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default OtterFigure;
